import React from 'react';
import {
  Box,
  VStack,
  Text,
  Flex,
  Badge,
  Tooltip,
  Icon,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Center,
  useToast,
} from '@chakra-ui/react';

import { SearchIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { MdCheckCircle, MdWarning, MdInfo, MdNotificationsActive } from 'react-icons/md';
import CustomCheckbox from './CustomCheckBox';
import baseUrl from 'Base_Url/baseUrl';

const TalukaItem = ({
  taluka,
  isSelected,
  onSelect,
  onCheck,
  showCheckbox = false,
  successColor,
  warningColor,
  infoColor,
  index,
  currentFY,
  selectedMonth
}) => {
  const getStatusIcon = () => {
    if (taluka.status === 'approved')
      return <Icon as={MdCheckCircle} color={successColor} />;
    if (taluka.status === 'rejected')
      return <Icon as={MdWarning} color={warningColor} />;
    if (taluka.status === 'submitted' || taluka.status === 'pending')
      return <Icon as={MdInfo} color={infoColor} />;
    return null;
  };


  // Function to get background color based on status
  const getBackgroundColor = () => {
    // Check if taluka has no data (no status or status is 'no_file')
    if (!taluka.status || taluka.status === 'no_file') {
      return 'orange.200'; // Light orange for no data
    }
    
    switch(taluka.status) {
      case 'approved':
        return 'green.200';
      case 'submitted':
      case 'pending':
        return 'blue.200';
      case 'rejected':
        return 'red.200';
      default:
        return '';
    }
  };

  // Function to get hover background color based on status
  const getHoverBackgroundColor = () => {
    if (isSelected) return 'blue.100'; // Selected state overrides status color
    
    // Check if taluka has no data
    if (!taluka.status || taluka.status === 'no_file') {
      return 'orange.300'; // Darker orange on hover for no data
    }
    
    switch(taluka.status) {
      case 'approved':
        return 'green.300';
      case 'submitted':
      case 'pending':
        return 'blue.300';
      case 'rejected':
        return 'red.300';
      default:
        return 'gray.50';
    }
  };


  const handleClick = (e) => {
    onSelect(taluka._id, e);
  };

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    const syntheticEvent = {
      target: {
        checked: !isSelected,
      },
      stopPropagation: () => { },
    };
    onCheck(taluka._id, !isSelected, syntheticEvent);
  };

  return (
    <Flex
      p={2}
      align="center"
      bg={getBackgroundColor()}
      borderLeft={isSelected ? '4px solid' : 'none'}
      borderLeftColor="blue.500"
      borderRadius="md"
      cursor="pointer"
      onClick={handleClick}
      _hover={{ bg: getHoverBackgroundColor() }}
      justify="space-between"
      boxShadow="sm"
      mb={0}
      transition="all 0.2s"
      position="relative"
      role="group"
    >
      <Flex align="center" flex={1}>
        { taluka.status === 'approved' || taluka.status === 'rejected'  || taluka.status === 'no_file'? '' : (showCheckbox && (
          <CustomCheckbox
            isChecked={isSelected}
            onChange={handleCheckboxChange}
            mr={3}
            size="md"
            colorScheme="blue"
          />
        ))}
        
        <Box flex={1}>
          <Flex align="center" gap={1}>
            <Text fontWeight={isSelected ? 'bold' : 'medium'} fontSize="sm">
              {taluka.nameOfOffice || taluka.name}
            </Text>
            {/* {getStatusIcon()} */}
          </Flex>
          <HStack spacing={2} mt={1}>
            {taluka.status && taluka.status !== 'no_file' && (
              <Badge
                colorScheme={
                  taluka.status === 'approved'
                    ? 'green'
                    : taluka.status === 'rejected'
                      ? 'red'
                      : taluka.status === 'submitted' 
                        ? 'blue'
                        : 'gray'
                      
                }
                variant="solid"
                px={2}
                borderRadius="full"
                fontSize="xs"
              >
                {taluka.status}
              </Badge>
            )}
          </HStack>
        </Box>
      </Flex>
      {/* {taluka.months && taluka.months.length > 0 && (
        <Tooltip label={`Available months: ${taluka.months.join(', ')}`}>
          <Badge
            colorScheme="purple"
            variant="outline"
            borderRadius="full"
            px={2}
          >
            {taluka.months.length}{' '}
            {taluka.months.length === 1 ? 'month' : 'months'}
          </Badge>
        </Tooltip>
      )} */}
      <ChevronRightIcon color="gray.400" ml={2} />
    </Flex>
  );
};

const TalukaPanel = ({
  talukasWithData,
  talukasWithoutData,
  selectedTalukaIds,
  onTalukaClick,
  onTalukaCheck,
  onSelectAllSubmitted, // Change this prop name
  showCheckboxes = false,
  successColor,
  warningColor,
  infoColor,
  selectedMonth,
  fileId,
  currentFY,
  scenario
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const Toast = useToast();
  const user = JSON.parse(localStorage.getItem('user'));

  // Filter talukas with 'submitted' status only
  const submittedTalukas = talukasWithData.filter(t => t.status === 'submitted');
  
  // Check if all submitted talukas are selected
  const allSubmittedSelected = 
    submittedTalukas.length > 0 && 
    submittedTalukas.every((t) => selectedTalukaIds.includes(t._id));

  const filteredWithData = talukasWithData.filter((t) =>
    (t.nameOfOffice || t.name || t.subDivision?.name || '')
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  const filteredWithoutData = talukasWithoutData.filter((t) =>
    (t.nameOfOffice || t.name || t.subDivision?.name || '')
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  // financial year generator 
  const getFY = (date) => new Date(date).getMonth() >= 3 ? new Date(date).getFullYear() : new Date(date).getFullYear() - 1;
  const FinancialYear = getFY(currentFY);

  // get current month 
  const getCurrentMonthName = () => {
    return new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date());
  };

  const currentMonth = getCurrentMonthName();
  
  // Updated handler to use the new selectAllSubmitted function
  const handleSelectAllChange = (e) => {
    e.stopPropagation();
    onSelectAllSubmitted(!allSubmittedSelected);
  };

const handleNotifyMissingFiles = async () => {
    try {
      if (!fileId) {
        Toast({
          title: 'Error',
          description: 'File type ID is missing',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (!selectedMonth && !currentMonth) {
        Toast({
          title: 'Error',
          description: 'Month is not selected',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const requestBody = {
        fileTypeId: fileId,
        financialYear: FinancialYear,
        month: selectedMonth || currentMonth,
      };

      Toast({
        title: 'Sending notification...',
        description: 'Please wait',
        status: 'info',
        duration: null,
        isClosable: true,
      });

      const response = await fetch(
        `${baseUrl}/notifications/missing-files/notify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(requestBody),
        },
      );

      Toast.closeAll();

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      Toast({
        title: 'Success',
        description: 'Notification sent successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error sending notification:', error);

      Toast({
        title: 'Error',
        description: error.message || 'Failed to send notification',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box
      w="350px"
      borderRight="2px"
      borderColor="gray.200"
      h="100%"
      display="flex"
      flexDirection="column"
      bg="white"
      boxShadow="md"
    >
      {/* Header */}
      <Box p={2} bg="gray.50" borderBottom="2px" borderColor="gray.200">
        <Flex justify="space-between" align="center" mb={3}>
          <Text fontWeight="bold" fontSize="lg">
            {scenario?.toLowerCase().includes('sdo') ? 'SDOs' : 'Talukas'}
          </Text>
          <Badge colorScheme="blue" px={3} py={1} borderRadius="full">
            {selectedTalukaIds.length} Selected
          </Badge>
        </Flex>

        {/* Search */}
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder={scenario?.toLowerCase().includes('sdo') ? 'Search SDOs...' : 'Search talukas...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            borderRadius="full"
            bg="white"
          />
        </InputGroup>

        {/* Select All Submitted Talukas - Using custom checkbox */}
        {submittedTalukas.length > 0 && (
          <Flex
            mt={1}
            align="center"
            p={1}
            bg={allSubmittedSelected ? 'blue.50' : 'transparent'}
            borderRadius="md"
            cursor="pointer"
            onClick={handleSelectAllChange}
            _hover={{ bg: 'gray.100' }}
          >
            <CustomCheckbox
              isChecked={allSubmittedSelected}
              onChange={handleSelectAllChange}
              mr={1}
              size="md"
              colorScheme="blue"
            />
            <Text fontSize="sm" fontWeight="medium" flex={1}>
              Select All Submitted ({submittedTalukas.length})
            </Text>
            <Badge colorScheme="blue" ml="auto">
              {submittedTalukas.length}
            </Badge>
          </Flex>
        )}
      </Box>

      {/* Taluka Lists */}
      <Box flex={1} overflowY="auto" p={1}>
        {/* With Data Section */}
        {filteredWithData.length > 0 && (
          <Box mb={1}>
            <Flex align="center" mb={2}>
              <Box w="3" h="3" bg="green.500" borderRadius="full" mr={2} />
              <Text fontSize="sm" fontWeight="bold" color="green.700">
                With Data ({filteredWithData.length})
              </Text>
            </Flex>
            <VStack spacing={1} align="stretch">
              {filteredWithData.map((taluka, index) => (
                <TalukaItem
                  key={taluka._id}
                  taluka={taluka}
                  isSelected={selectedTalukaIds.includes(taluka._id)}
                  onSelect={onTalukaClick}
                  onCheck={onTalukaCheck}
                  showCheckbox={showCheckboxes}
                  successColor={successColor}
                  warningColor={warningColor}
                  infoColor={infoColor}
                  index={index}
                />
              ))}
            </VStack>
          </Box>
        )}

        {/* Without Data Section */}
        {filteredWithData.length === 0 && filteredWithoutData.length === 0 && (
          <Center p={8}>
            <Text color="gray.500">
              {scenario?.toLowerCase().includes('sdo')
                ? 'No SDOs match your search'
                : 'No talukas match your search'}
            </Text>
          </Center>
        )}
      </Box>
      <Box flex={1} overflowY="auto" p={1} border={1}>
        {filteredWithoutData.length > 0 && (
          <Box>
            <Flex align="center" mb={2} style={{justifyContent : 'space-between'}}>
              <Text fontSize="sm" fontWeight="bold" color="orange.700">
                Without Data ({filteredWithoutData.length})
              </Text>
             { user.role === "schemeAdmin" && (
                <Tooltip label="Send notification for missing files">
                  <Box
                    w={6}
                    h={6}
                    borderRadius="full"
                    bg="white"
                    cursor="pointer"
                    onClick={handleNotifyMissingFiles}
                    _hover={{ bg: 'orange.200', transform: 'scale(1.1)' }}
                    transition="all 0.2s"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <MdNotificationsActive size={30} color="#dd6b20" />
                  </Box>
                </Tooltip>
              ) }
            </Flex>
            <VStack spacing={1} align="stretch">
              {filteredWithoutData.map((taluka, index) => (
                <TalukaItem
                  key={taluka._id}
                  taluka={taluka}
                  isSelected={selectedTalukaIds.includes(taluka._id)}
                  onSelect={onTalukaClick}
                  onCheck={onTalukaCheck}
                  showCheckbox={showCheckboxes}
                  successColor={successColor}
                  warningColor={warningColor}
                  infoColor={infoColor}
                  index={index}
                />
              ))}
            </VStack>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TalukaPanel;

